import * as moment from 'moment-timezone';

export const STR_FORMAT = 'DD MMM YYYY';
export const ISO_DATE = 'YYYY-MM-DD';
export const ISO_DATE_TIME = 'YYYY-MM-DDTHH:mm:ssZ';
export const TIMESTAMP_DATE_TIME = 'YYYY-MM-DD HH:mm:ss';
export const TIME_12_HOURS = 'hh:mm A';
export const ISO_TZ = 'Z';
export const WEEK_DAY = 'dddd';
export const MONTH_NAME = 'MMMM';
export const MINUTES_PER_SLOT = 15;
export const UTC_OFFSET = '+00:00';
export const MONTH_DEY_YEAR = 'MMM DD, YYYY';
export const WEEKS_DAYS = [
   'Monday',
   'Tuesday',
   'Wednesday',
   'Thursday',
   'Friday',
   'Saturday',
   'Sunday',
];
export const TZ = 'America/New_York';

export const currentTimeISO = () => {
   return moment().utcOffset(0).format(ISO_DATE_TIME);
};

export const timeToISO = (time) => {
   return moment(time).format(TIMESTAMP_DATE_TIME);
};

export const timeToISOInEST = (time) => {
   return moment(time).tz(TZ).format(ISO_DATE_TIME);
};

export const timeToStr = (time) => {
   return moment(time).format(STR_FORMAT);
};

export const startEndPeriod = (startTime, endTime, full = false) => {
   const startYear = moment(startTime).format('YYYY');
   const endYear = moment(endTime).format('YYYY');
   const mounthFormat = full ? 'MMMM' : 'MMM';

   if (startYear === endYear) {
      return `${moment(startTime).tz(TZ).format('DD')}th ${moment(startTime)
         .tz(TZ)
         .format(mounthFormat)} - ${moment(endTime).tz(TZ).format('DD')}th ${moment(endTime)
         .tz(TZ)
         .format(mounthFormat)} ${endYear}`;
   }
   return `${moment(startTime).tz(TZ).format('DD')}th ${moment(startTime)
      .tz(TZ)
      .format(mounthFormat)} ${startYear} - ${moment(endTime).tz(TZ).format('DD')}th ${moment(
      endTime,
   )
      .tz(TZ)
      .format(mounthFormat)} ${endYear}`;
};

export const dateMDYtoEST = (date) => {
   return moment(date).tz(TZ).format(MONTH_DEY_YEAR);
};

export const dateToEST = (date) => {
   return moment(moment(date).utcOffset(0)).tz(TZ).toDate();
};

const parseCourseCalendarDate = (value) => {
  const text = String(value || "");
  const isoDate = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const slashDate = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);

  if (isoDate) {
    return moment.utc(`${isoDate[1]}-${isoDate[2]}-${isoDate[3]}`);
  }

  if (slashDate) {
    return moment.utc(
      `${slashDate[3]}-${slashDate[1].padStart(2, "0")}-${slashDate[2].padStart(2, "0")}`,
    );
  }

  return moment.utc(value);
};

export const formatUniversalDate = (value) => {
  if (!value) return "";

  const date = parseCourseCalendarDate(value);

  return date.isValid() ? date.format("D MMMM YYYY") : String(value);
};

const getOrdinal = (day) => {
  if (day > 3 && day < 21) return "th";

  switch (day % 10) {
    case 1: return "st";
    case 2: return "nd";
    case 3: return "rd";
    default: return "th";
  }
};

export const formatCourseDateWithOrdinal = (value) => {
  const date = parseCourseCalendarDate(value);

  if (!date.isValid()) return String(value || "");

  const dayNumber = date.date();
  const monthName = date.format("MMM");
  const year = date.year();

  return `${dayNumber}${getOrdinal(dayNumber)} ${monthName} ${year}`;
};

const getCourseDurationConfig = (course) => {
  const duration = course?.duration || course?.course_type?.duration;

  if (typeof duration === "number") {
    return { value: duration, unit: "weeks" };
  }

  if (duration && typeof duration === "object") {
    const value = duration.value ?? duration.durationValue ?? duration.weeks;
    const unit = duration.unit || duration.durationUnit || "weeks";

    if (value != null) return { value: Number(value), unit };
  }

  const value = course?.durationValue ?? course?.course_type?.durationValue;
  const unit = course?.durationUnit ?? course?.course_type?.durationUnit ?? "weeks";

  return value != null ? { value: Number(value), unit } : null;
};

export const calculateCourseEndDate = (startDate, durationValue, durationUnit = "weeks") => {
  const dateParts = String(startDate || "").match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (!dateParts || !Number.isFinite(Number(durationValue))) return "";

  const date = moment.utc(`${dateParts[1]}-${dateParts[2]}-${dateParts[3]}`);
  const unit = String(durationUnit).toLowerCase();

  if (unit.startsWith("month")) {
    date.add(Number(durationValue), "months");
  } else if (unit.startsWith("day")) {
    date.add(Number(durationValue), "days");
  } else {
    date.add(Number(durationValue) * 7, "days");
  }

  return date.format(ISO_DATE);
};

export const getCourseCalendarDates = (course, regionName) => {
  const upcomingPrograms = course?.course_type?.upcoming_programs || course?.upcoming_programs;
  const upcomingCohorts = upcomingPrograms?.flatMap(
    (program) => program?.upcomingCohorts || [],
  ) || [];
  const selectedCohort = upcomingCohorts.find((cohort) =>
    regionName && cohort?.region?.name?.includes(regionName),
  ) || upcomingCohorts[0];
  const cohortDuration = selectedCohort?.duration?.[0];
  const startDate = cohortDuration?.Start || course?.startDate || course?.start_date;
  const fallbackEndDate = cohortDuration?.End || course?.endDate || course?.end_date;
  const duration = getCourseDurationConfig(course);
  const calculatedEndDate = duration && startDate
    ? calculateCourseEndDate(startDate, duration.value, duration.unit)
    : "";

  return {
    startDate,
    endDate: calculatedEndDate || fallbackEndDate,
    durationValue: duration?.value,
    durationUnit: duration?.unit,
  };
};

export const dateFromInWeeks = (startDate, endDate) => {
  const end = moment.utc(endDate).startOf('day');
  const start = moment.utc(startDate).startOf('day');
  const calendarDays = end.diff(start, 'days');

  return Math.ceil(calendarDays / 7);
};

export const getTimeHM = (time) => {
   return moment(time).tz(TZ).format(TIME_12_HOURS);
};

export const convertTo12HourFormat = (timeStr) => {
  if (!timeStr) return "";
  return moment(timeStr, 'HH:mm:ss').format('h:mmA');
};

export const convertDateToValidFormat = (startDate) => {
  return formatCourseDateWithOrdinal(startDate);
};

export const formatTimeRange = (from, to) => {
  const formattedFrom = moment(from, 'HH:mm:ss').format('h:mm A');
  const formattedTo = moment(to, 'HH:mm:ss').format('h:mm A');
  return { formattedFrom, formattedTo };
};

export const formatBackendTimeRange = (from, to) => {
  const formattedFrom = moment.utc(from, ['HH:mm:ss', 'HH:mm']).format('h:mm A');
  const formattedTo = moment.utc(to, ['HH:mm:ss', 'HH:mm']).format('h:mm A');
  return { formattedFrom, formattedTo };
};

export const getCourseTimezoneLabel = (regionName = '') => {
  if (/(europe|africa|uk|united kingdom)/i.test(regionName)) return 'BST';
  if (/(us|united states|canada)/i.test(regionName)) return 'EST';
  return '';
};

export const formatTrainingDays = (trainingDays = [], regionName = '') => {
  const groupedSessions = trainingDays.reduce((groups, session) => {
    const key = `${session?.from || ''}-${session?.to || ''}`;
    const group = groups.find((item) => item.key === key);

    if (group) {
      group.days.push(session.day?.slice(0, 3));
    } else {
      groups.push({ key, days: [session.day?.slice(0, 3)], from: session.from, to: session.to });
    }

    return groups;
  }, []);

  const timezone = getCourseTimezoneLabel(regionName);

  return groupedSessions
    .map((session) => {
      const { formattedFrom, formattedTo } = formatBackendTimeRange(session.from, session.to);
      return `${session.days.join(', ')} ${formattedFrom} - ${formattedTo}${timezone ? ` ${timezone}` : ''}`;
    })
    .join('; ');
};

export const generateLongDuration = (startDate, endDate) => {
   const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const formatDay = (day) => {
    if (day >= 11 && day <= 13) {
      return `${day}th`;
    }
    switch (day % 10) {
      case 1: return `${day}st`;
      case 2: return `${day}nd`;
      case 3: return `${day}rd`;
      default: return `${day}th`;
    }
  };

  const formatMonth = (month) => months[month - 1];

  const formatDate = (dateString) => {
    const date = moment.utc(dateString);
    const day = formatDay(date.date());
    const month = formatMonth(date.month() + 1);
    const year = date.year();
    return `${day} ${month} ${year}`;
  };

  const formattedStartDate = formatDate(startDate);
  const formattedEndDate = formatDate(endDate);

  return `${formattedStartDate} - ${formattedEndDate}`;
};

export const calculateCourseDuration = (startDate, endDate) => {
  const _MS_PER_DAY = 1000 * 60 * 60 * 24;
  const start_course_date = Date.UTC(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate()
  );
  const end_course_date = Date.UTC(
    endDate.getFullYear(),
    endDate.getMonth(),
    endDate.getDate()
  );
  return Math.floor((end_course_date - start_course_date) / _MS_PER_DAY);
};

export const convertDateToDays = (startDate, endDate) => {
  const start_Date = moment(startDate);
  const end_Date = moment(endDate);

  if (start_Date.isValid() && end_Date.isValid()) {
    const daysDifference = end_Date.diff(start_Date, 'days');
    return `${daysDifference} day${daysDifference !== 1 ? 's' : ''}`;
  } else {
    return "Invalid Date Range";
  }
};

export const convertDateToWeeks = (startDate, endDate) => {
  const start_Date = moment(startDate);
  const end_Date = moment(endDate);

  if (start_Date.isValid() && end_Date.isValid()) {
    const weeksDifference = dateFromInWeeks(startDate, endDate);
    return `${weeksDifference} week${weeksDifference !== 1 ? 's' : ''}`;
  } else {
    return "Invalid Date Range";
  }
};

export const generateShortDuration = (startDate, endDate) => {
  const monthsShort = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ];

  const formatDay = (day) => {
    if (day >= 11 && day <= 13) {
      return `${day}th`;
    }
    switch (day % 10) {
      case 1: return `${day}st`;
      case 2: return `${day}nd`;
      case 3: return `${day}rd`;
      default: return `${day}th`;
    }
  };

  const formatMonth = (month) => monthsShort[month - 1];

  const formatDate = (dateString) => {
    const date = moment.utc(dateString);
    const day = formatDay(date.date());
    const month = formatMonth(date.month() + 1);
    return `${day} ${month}`;
  };

  const formattedStartDate = formatDate(startDate);
  const formattedEndDate = formatDate(endDate);

  return `${formattedStartDate} - ${formattedEndDate} ${moment.utc(startDate).year()}`;
}

export const convertTimeToRegion = (time, region) => {
  let timeZone;
  
  // If region is an object from the API, use its timeZone property
  if (typeof region === 'object' && region?.timeZone) {
    // Map specific short forms to standard names if necessary
    if (region.timeZone === 'EST') timeZone = 'America/New_York';
    else if (region.timeZone === 'GMT') timeZone = 'UTC';
    else timeZone = region.timeZone;
  } 
  // Fallback map for hardcoded region names (strings)
  else if (region === 'US & Canada') {
    timeZone = 'America/New_York';
  } else if (region === 'Europe - Africa') {
    timeZone = 'UTC';
  } else {
    timeZone = 'UTC';
  }

  return moment.tz(time, 'HH:mm:ss', timeZone).format('h:mmA z');
};

export const formatDeadline = (dateString) => {
  if (!dateString) return "";
  const date = moment(dateString);
  const day = date.date();
  
  const formatDay = (d) => {
    if (d >= 11 && d <= 13) return `${d}th`;
    switch (d % 10) {
      case 1: return `${d}st`;
      case 2: return `${d}nd`;
      case 3: return `${d}rd`;
      default: return `${d}th`;
    }
  };

  return `${formatDay(day)} ${date.format('MMMM')}, ${date.format('YYYY')} ${date.format('hh:mmA')}`;
};
